import { Box, Flex, Td, Text, Tr } from '@chakra-ui/react';
import UserName from 'src/components/User/UserName';
import ProxyUserMenu from 'src/components/ProxyUser/ProxyUserMenu';
import { RenderUserBadgeList } from './UserBadgeList';
import SubmittedAuthorDetailsModal from 'src/components/Submission/AuthorsTab/AuthorProfileModal/SubmittedAuthorDetailsModal';
import {
  IJournalWiseUserRoleList,
  IUsersListItemResponse
} from '@publishing-engine/domain/models';
import { ITags } from '@publishing-engine/domain/models/organization';
import { AdHocModalOpen } from 'src/components/Submission/AuthorsTab/ComposeEmailModal';
import UserItem from './UserItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import AddUser from 'src/components/User/AddUser/AddUser';
import { formatDate } from 'src/utils/date';
import { ButtonIcon } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import { fetchAuthorProfileData } from 'src/api/queries';
import { IAuthor } from '@publishing-engine/domain/types/AuthorType';

const formatUserDate = (date?: string) =>
  date ? formatDate(new Date(date)) : '-';

const UserRowItem = ({
  user,
  manageTags,
  setOpenSendEmailModal,
  selectedUser,
  setSelectedUser,
  orgId,
  workspaceId,
  canEditUsers,
  showEditIconOnly,
  clampAffiliation,
  showTimestamps,
  backButtonText
}: {
  user: IUsersListItemResponse;
  manageTags:
    | {
        collection: ITags[];
      }
    | null
    | undefined;
  setOpenSendEmailModal: (data: AdHocModalOpen) => void;
  selectedUser: IUsersListItemResponse | null;
  setSelectedUser: (user: IUsersListItemResponse | null) => void;
  orgId: string;
  workspaceId: string;
  canEditUsers: boolean;
  showEditIconOnly: boolean;
  clampAffiliation: boolean;
  showTimestamps: boolean;
  backButtonText: string;
}) => {
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const updatedUserTags = manageTags?.collection.filter(tag =>
    user.tags.some(userTag => userTag.id === tag.id)
  );
  const isProxyUser = user.isProxyUser;

  const { data: authorData } = useQuery<IAuthor>({
    queryKey: ['workspace-author-profile', orgId, user.userId],
    queryFn: () => fetchAuthorProfileData(orgId, user.userId),
    enabled: Boolean(orgId && user.userId)
  });

  return (
    <>
      {selectedUser?.userId && (
        <SubmittedAuthorDetailsModal
          backButtonText={backButtonText}
          userId={selectedUser.userId}
          onBackClick={() => setSelectedUser(null)}
          roles={selectedUser.roles}
          showReviewerActivityForEachJournal
        />
      )}
      {canEditUsers && (
        <AddUser
          title="Edit User"
          submitButtonText="Save Changes"
          isOpen={isEditUserOpen}
          onClose={() => setIsEditUserOpen(false)}
          organizationId={orgId}
          workspaceId={workspaceId}
          mode="edit"
          userId={user.userId}
        />
      )}

      <Tr
        key={user?.userId}
        gap={2}
        data-testid="submissions-table-row"
        background="white"
      >
        <Td minWidth="180px">
          <Box maxWidth={clampAffiliation ? '320px' : '100%'}>
            <Box onClick={() => setSelectedUser(user)} cursor="pointer">
              <UserName
                firstName={user?.firstName}
                lastName={user?.lastName}
                tooltipLabel="View User Profile"
                lastSentStatus={authorData?.user?.lastSentEmailStatus}
                enableIcon={Object.hasOwn(authorData?.user ?? {}, 'isEmailDelivered')}
                isEmailDelivered={authorData?.user?.isEmailDelivered}
                timeStamp={authorData?.user?.lastSentEmailTimeStamp}
              />
            </Box>
            <Text textStyle="textContext" isTruncated={clampAffiliation}>
              {user?.email}
            </Text>
            {clampAffiliation ? (
              <Text
                textStyle="textContext"
                overflow="hidden"
                textOverflow="ellipsis"
                display="-webkit-box"
                sx={{
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  whiteSpace: 'normal'
                }}
              >
                {user?.affiliation}
              </Text>
            ) : (
              <Text textStyle="textContext">{user?.affiliation}</Text>
            )}
            <Box marginTop={1}>
              <RenderUserBadgeList
                data={updatedUserTags ?? []}
                hasIcon={true}
                defaultLimit={2}
              />
            </Box>
          </Box>
        </Td>

        <Td textStyle="textBody">
          {<UserItem data={user?.journals as IJournalWiseUserRoleList[]} />}
        </Td>
        <Td
          textStyle="textBody"
          textAlign="start"
          data-testid="workspace-member"
        >
          {user?.isWorkspaceMember ? <FontAwesomeIcon icon={faCheck} /> : ''}
        </Td>
        {showTimestamps && (
          <Td textStyle="textBody" whiteSpace="nowrap">
            {formatUserDate(user.createdAt)}
          </Td>
        )}
        {showTimestamps && (
          <Td textStyle="textBody" whiteSpace="nowrap">
            {formatUserDate(user.updatedAt)}
          </Td>
        )}
        <Td textStyle="textBody">
          <Flex justifyContent={'flex-end'}>
            {showEditIconOnly ? (
              canEditUsers ? (
                <ButtonIcon
                  variant="secondary"
                  ariaLabel="Edit User"
                  icon={<FontAwesomeIcon icon={faPenToSquare} />}
                  onClick={() => setIsEditUserOpen(true)}
                />
              ) : null
            ) : (
              <ProxyUserMenu
                isProxyUser={isProxyUser}
                handleClick={() => setSelectedUser(user)}
                user={user}
                setOpenSendEmailModal={setOpenSendEmailModal}
                authorData={authorData}
              />
            )}
          </Flex>
        </Td>
      </Tr>
    </>
  );
};

export default UserRowItem;
